<template>
  <div class="container">
    <div class="glass-card question-card">
      <div class="question-content" id="questionText">{{ problem }}</div>
      
      <!-- 题目信息 -->
      <div class="problem-info">
        <div class="problem-info-item">
          {{ problemSource }}
        </div>
      </div>
    </div>

    <!-- 系统分析卡片 -->
    <div class="glass-card analysis-card">
      <div class="analysis-title">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z"></path>
          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z"></path>
        </svg>
        DEEPSEEK分析
        <div class="model-selector">
          <label class="switch">
            <input type="checkbox" v-model="useR1Model">
            <span class="slider round"></span>
          </label>
          <span id="modelLabel">{{ selectedModelLabel }}</span>
        </div>
      </div>
      <div class="analysis-content">
        <!-- 只在初始状态显示placeholder -->
        <div v-if="analysisStatus === null" class="analysis-placeholder">
          完成思考，点击开始答题，录音随即启动。<br>
          完成作答后，系统自动生成详尽分析报告。<br>
          评分仅作参考，分析录音会转文本去除语气词和停顿。<br>
        </div>
        <!-- 任何其他状态都显示分析内容，包括状态消息 -->
        <div v-else v-html="formattedAnalysis" class="analysis-result"></div>
        
        <!-- 分析内容中的控制按钮 -->
        <div class="analysis-controls" v-if="analysisStatus !== 'success' && analysisStatus !== 'loading' && analysisStatus !== 'uploading'">
          <button class="btn btn-primary" @click="startRecording" v-if="!isRecording">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="M12 2a3 3 0 0 0-3 3v7a3 3 0 0 0 6 0V5a3 3 0 0 0-3-3Z"></path>
              <path d="M19 10v2a7 7 0 0 1-14 0v-2"></path>
              <line x1="12" y1="19" x2="12" y2="22"></line>
            </svg>
            开始答题
          </button>
          <button class="btn btn-warning" @click="pauseRecording" v-if="isRecording && !isPaused">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <rect x="6" y="4" width="4" height="16"></rect>
              <rect x="14" y="4" width="4" height="16"></rect>
            </svg>
            暂停答题
          </button>
          <button class="btn btn-success" @click="resumeRecording" v-if="isRecording && isPaused">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polygon points="5 3 19 12 5 21 5 3"></polygon>
            </svg>
            继续答题
          </button>
          <button class="btn btn-danger" @click="stopAndUpload" v-if="isRecording && isPaused">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <polyline points="22 12 18 12 15 21 9 3 6 12 2 12"></polyline>
            </svg>
            上传录音
          </button>
        </div>
      </div>
    </div>

    <!-- 麦克风不可用时的备选输入区域 -->
    <div id="fallbackInputContainer" v-if="!isMicrophoneAvailable" class="glass-card mt-3">
      <div style="margin-bottom: 10px; font-weight: 500; color: #64748b;">
        麦克风不可用，请直接输入您的答案：
      </div>
      <textarea v-model="textInput" style="width: 100%; min-height: 150px; padding: 12px; border-radius: 8px; border: 1px solid rgba(203, 213, 225, 0.5); resize: vertical;"></textarea>
      <div style="display: flex; justify-content: flex-end; margin-top: 10px;">
        <button class="btn btn-primary" @click="submitText" style="padding: 8px 16px;">
          提交答案
        </button>
      </div>
    </div>

    <!-- 录音指示器 -->
    <div class="recording-indicator" :class="{ active: isRecording && !isPaused }">
      <div class="pulse"></div>
      <span>正在录音</span>
      <span class="timer">{{ formattedRecordingTime }}</span>
    </div>
    
    <!-- 聊天输入框 -->
    <div class="chat-input-container" v-if="showChatInput || analysisStatus === 'success'">
      <input type="text" v-model="followUpMessage" class="chat-input" placeholder="输入消息..." @keyup.enter="sendFollowUp">
      <button class="send-btn" @click="sendFollowUp" :disabled="!followUpMessage.trim() || followUpStatus === 'sending'">
        <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <line x1="22" y1="2" x2="11" y2="13"></line>
          <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
        </svg>
      </button>
    </div>
  </div>
</template>

<script>
import AIService from '@/services/ai.service';
import { marked } from 'marked'; // 导入marked库

export default {
  name: 'AIMode',
  data() {
    return {
      // 题目信息
      problem: '正在加载题目...',
      problemYear: '',
      problemType: '',
      problemSource: '',
      
      // 录音相关
      isRecording: false,
      isPaused: false,
      recordingTime: 0,
      recorder: null,
      audioChunks: [],
      recordingInterval: null,
      isMicrophoneAvailable: true,
      
      // 文本输入
      textInput: '',
      
      // 分析状态
      analysisStatus: null, // loading, uploading, success, error
      analysisError: '',
      analysisResult: '',
      statusFilePath: '',
      analysisFilePath: '',
      showChatInput: false, // 添加一个控制聊天输入框显示的变量
      
      // 追问相关
      followUpMessage: '',
      followUpStatus: null, // sending, success, error
      
      // 模型选择
      useR1Model: true,
    };
  },
  computed: {
    selectedModel() {
      return this.useR1Model ? 'deepseek-r1' : 'deepseek-v3';
    },
    selectedModelLabel() {
      return this.useR1Model ? 'R1模型' : 'V3模型';
    },
    formattedRecordingTime() {
      const minutes = Math.floor(this.recordingTime / 60);
      const seconds = this.recordingTime % 60;
      return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
    },
    formattedAnalysis() {
      if (!this.analysisResult) return '';
      
      // 已经在getAnalysisFile方法中处理为HTML，直接返回
      let result = this.analysisResult;
      
      // 正则表达式匹配所有【】括号中的内容
      const bracketPattern = /【(.+?)】/g;
      
      // 先替换**提出对策**这样的粗体标记
      result = result.replace(/\*\*([^*]+)\*\*/g, '<strong>$1</strong>');
      
      // 处理带序号的列表项，确保不会破坏markdown渲染
      result = result.replace(/(\d+\.\s+)(<strong>.*?<\/strong>)/g, '<span class="list-item">$1$2</span>');
      
      // 给所有【】包裹的内容添加特殊样式类，并确保它们都左对齐
      result = result.replace(bracketPattern, match => {
        const label = match.substring(1, match.length - 1); // 去掉【】
        
        // 特殊处理【DEEPSEEK分析】和【识别结果】使其字体更大
        if (label === 'DEEPSEEK分析' || label === '语音识别') {
          return `<div class="deepseek-section"><span class="deepseek-analysis-title">【${label}】</span></div>`;
        }
        
        // 对所有其他【】标签使用统一样式
        return `<div class="deepseek-section"><span class="bracketed-content">【${label}】</span></div>`;
      });
      
      // 确保在markdown格式的内容前能正确关闭deepseek-section
      result = result.replace(/<\/div>(\s*)<p>/g, '</div>$1<p style="text-align:left;">');
      
      return result;
    }
  },
  created() {
    this.loadRandomProblem();
    this.checkMicrophoneAvailability();
  },
  methods: {
    checkMicrophoneAvailability() {
      // 检查是否在安全上下文中（HTTPS）
      if (!window.isSecureContext) {
        console.error("页面不在安全上下文中运行，MediaDevices API可能不可用");
        this.isMicrophoneAvailable = false;
        return;
      }
      
      // 检查浏览器是否支持mediaDevices API
      if (!navigator.mediaDevices) {
        console.error("navigator.mediaDevices 不可用");
        this.isMicrophoneAvailable = false;
        return;
      }
      
      // 尝试获取麦克风权限
      navigator.mediaDevices.getUserMedia({ audio: true })
        .then(stream => {
          console.log("成功获取麦克风权限");
          this.isMicrophoneAvailable = true;
          // 释放媒体流
          stream.getTracks().forEach(track => track.stop());
        })
        .catch(error => {
          console.error('获取麦克风权限失败:', error);
          this.isMicrophoneAvailable = false;
        });
    },
    
    loadRandomProblem() {
      console.log('Fetching random problem from AI service');
      AIService.getRandomProblem()
        .then(response => {
          console.log('Random problem response:', response);
          const data = response.data;
          this.problem = data.detail;
          this.problemYear = data.year;
          this.problemType = data.type;
          this.problemSource = data.source;
        })
        .catch(error => {
          console.error('Error loading problem:', error);
          this.problem = '加载题目失败，请刷新页面重试。';
        });
    },
    
    startRecording() {
      console.log("开始录音函数被调用");
      
      // 检查MediaRecorder是否可用
      if (typeof MediaRecorder === 'undefined') {
        console.error("MediaRecorder API不可用");
        alert("您的浏览器不支持MediaRecorder API，无法进行录音。请使用最新版本的Chrome、Firefox或Edge浏览器。");
        return;
      }
      
      // 请求麦克风权限并设置采样率为16000Hz
      const audioConstraints = {
        audio: {
          sampleRate: 16000,
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true
        }
      };
      
      navigator.mediaDevices.getUserMedia(audioConstraints)
        .then(stream => {
          console.log("成功获取麦克风权限");
          
          try {
            // 配置MediaRecorder，使用WAV格式
            const options = { 
              mimeType: 'audio/wav',
              audioBitsPerSecond: 256000 
            };
            
            // 如果浏览器不支持WAV格式，尝试使用WebM格式
            if (!MediaRecorder.isTypeSupported('audio/wav')) {
              console.log("浏览器不支持WAV格式录音，使用WebM格式");
              options.mimeType = 'audio/webm';
            }
            
            this.recorder = new MediaRecorder(stream, options);
            console.log("MediaRecorder实例创建成功，使用格式：", options.mimeType);
            this.audioChunks = [];
            
            // 收集录音数据
            this.recorder.ondataavailable = (event) => {
              this.audioChunks.push(event.data);
            };
            
            // 开始录音
            this.recorder.start();
            console.log("录音开始");
            this.isRecording = true;
            this.isPaused = false;
            
            // 开始计时
            this.startTimer();
          } catch (e) {
            console.error("创建MediaRecorder实例失败:", e);
            alert("创建录音器失败: " + e.message);
          }
        })
        .catch(error => {
          console.error('获取麦克风权限失败:', error);
          
          let errorMessage = '无法访问麦克风，请确保已授予麦克风权限。';
          
          // 根据错误类型提供更具体的提示
          if (error.name === 'NotAllowedError' || error.name === 'PermissionDeniedError') {
            errorMessage = '麦克风访问被拒绝。请在浏览器设置中允许访问麦克风，然后刷新页面重试。';
          } else if (error.name === 'NotFoundError' || error.name === 'DevicesNotFoundError') {
            errorMessage = '未找到麦克风设备。请确保您的设备有麦克风并且正常工作。';
          } else if (error.name === 'NotReadableError' || error.name === 'TrackStartError') {
            errorMessage = '无法读取麦克风。可能是因为麦克风被其他应用程序占用，请关闭其他使用麦克风的应用后重试。';
          } else if (error.name === 'SecurityError') {
            errorMessage = '安全错误。请确保您通过HTTPS访问此页面。';
          }
          
          alert(errorMessage + '\n\n技术详情: ' + error.name + ' - ' + error.message);
        });
    },
    
    pauseRecording() {
      if (this.recorder && this.isRecording) {
        this.recorder.pause();
        this.isPaused = true;
        clearInterval(this.recordingInterval);
      }
    },
    
    resumeRecording() {
      if (this.recorder && this.isRecording && this.isPaused) {
        this.recorder.resume();
        this.isPaused = false;
        this.startTimer();
      }
    },
    
    stopAndUpload() {
      if (this.recorder) {
        this.recorder.stop();
        this.isRecording = false;
        this.isPaused = false;
        clearInterval(this.recordingInterval);

        // 立即隐藏控制按钮并更新状态
        this.analysisStatus = 'loading';
        // 显示聊天输入框
        this.showChatInput = true;
        
        // 初始化分析结果并添加上传状态消息
        this.analysisResult = '';
        this.updateAnalysisContent('正在上传录音...');
        
        // 创建Blob并上传
        this.recorder.onstop = () => {
          // 检查录音格式
          let audioType = this.recorder.mimeType;
          let fileExtension = 'wav';
          
          if (!audioType.includes('audio/wav')) {
            console.log("使用非WAV格式录音，MIME类型:", audioType);
            fileExtension = 'webm';
          }
          
          const audioBlob = new Blob(this.audioChunks, { type: audioType });
          const formData = new FormData();
          formData.append('audio', audioBlob, `recording.${fileExtension}`);
          formData.append('model', this.selectedModel);

          // 使用AIService上传文件
          AIService.uploadAudio(formData)
            .then(response => {
              if (response.data && response.data.success) {
                // 保存分析文件路径，供后续使用
                this.statusFilePath = response.data.status_file;
                this.analysisFilePath = response.data.analyze_file;
                
                // 清除上一个状态消息并显示新的状态消息
                this.analysisResult = '';
                this.updateAnalysisContent('录音上传完毕，正在进行语音识别...');
                
                // 轮询获取识别文本
                this.pollTextStatus();
              } else {
                throw new Error(response.data.message || '上传失败');
              }
            })
            .catch(error => {
              this.analysisStatus = 'error';
              this.analysisError = error.message || '上传失败，请重试';
              this.analysisResult = '';
              this.updateAnalysisContent(`上传失败: ${error.message || '请重试'}`);
              console.error('Upload error:', error);
            });
        };
      }
    },
    
    submitText() {
      if (!this.textInput.trim()) return;
      
      this.analysisStatus = 'loading';
      this.analysisResult = '';
      this.updateAnalysisContent('正在提交文本...');
      
      const formData = new FormData();
      formData.append('text', this.textInput);
      formData.append('model', this.selectedModel);
      
      AIService.uploadAudio(formData)
        .then(response => {
          if (response.data && response.data.success) {
            this.statusFilePath = response.data.status_file;
            this.analysisFilePath = response.data.analyze_file;
            
            // 更新状态文本
            this.analysisResult = '';
            this.updateAnalysisContent('文本已提交，正在分析...');
            this.analysisStatus = 'loading';
            
            // 跳过文本识别阶段，直接获取分析结果
            this.getAnalysisFile();
          } else {
            throw new Error(response.data.message || '提交失败');
          }
        })
        .catch(error => {
          this.analysisStatus = 'error';
          this.analysisError = error.message || '提交文本失败，请重试';
          this.analysisResult = '';
          this.updateAnalysisContent(`提交文本失败: ${error.message || '请重试'}`);
          console.error('Text submission error:', error);
        });
    },
    
    pollTextStatus() {
      if (!this.statusFilePath) return;
      
      let pollCount = 0;
      const maxPollCount = 30; // 最多轮询30次
      
      const fetchTextStatus = () => {
        pollCount++;
        console.log(`[文本识别] 轮询第 ${pollCount} 次: ${this.statusFilePath}`);
        
        AIService.getTextStatus(this.statusFilePath)
          .then(response => {
            if (response.data && response.data.success) {
              const status = response.data.status;
              
              if (status === 'completed') {
                console.log('[文本识别] 状态显示识别已完成，开始获取分析结果');
                // 不清除之前的状态消息，添加新状态
                this.updateAnalysisContent('语音识别完成，正在分析...');
                this.analysisStatus = 'loading';
                // 已经完成语音识别，可以显示聊天输入框
                this.showChatInput = true;
                this.getAnalysisFile();
              } else if (status.startsWith('failed:') || status.startsWith('error:')) {
                console.error('[文本识别] 处理失败:', status);
                this.analysisResult = '';
                this.updateAnalysisContent(`处理失败: ${status.split(':')[1] || '识别失败'}`);
                throw new Error(status.split(':')[1] || '识别失败');
              } else if (status === 'processing') {
                console.log('[文本识别] 正在处理中...');
                if (pollCount < maxPollCount) {
                  setTimeout(fetchTextStatus, 1000); // 缩短轮询间隔到1秒，加快响应速度
                } else {
                  console.log('[文本识别] 超出最大轮询次数');
                  this.updateAnalysisContent('文本识别超时，正在尝试获取分析结果...');
                  this.analysisStatus = 'loading';
                  this.getAnalysisFile(); // 即使超时也尝试获取分析结果
                }
              } else {
                // 未知状态，继续轮询
                if (pollCount < maxPollCount) {
                  setTimeout(fetchTextStatus, 1000); // 缩短轮询间隔
                } else {
                  this.analysisStatus = 'loading';
                  this.updateAnalysisContent('处理超时，正在尝试获取分析结果...');
                  this.getAnalysisFile(); // 即使状态未知也尝试获取分析结果
                }
              }
            } else {
              console.log('[文本识别] 请求失败:', response.data.message);
              
              if (pollCount < maxPollCount) {
                setTimeout(fetchTextStatus, 1000); // 缩短轮询间隔
              } else {
                this.analysisStatus = 'loading';
                this.updateAnalysisContent('请求失败，正在尝试获取分析结果...');
                this.getAnalysisFile(); // 即使失败也尝试获取分析结果
              }
            }
          })
          .catch(error => {
            console.error('[文本识别] 请求异常:', error);
            
            if (pollCount < maxPollCount) {
              setTimeout(fetchTextStatus, 1000); // 缩短轮询间隔
            } else {
              this.analysisStatus = 'loading';
              this.updateAnalysisContent('请求异常，正在尝试获取分析结果...');
              this.getAnalysisFile(); // 即使出错也尝试获取分析结果
            }
          });
      };
      
      // 开始轮询
      setTimeout(fetchTextStatus, 500); // 缩短初始等待时间
    },
    
    getAnalysisFile() {
      if (!this.analysisFilePath) {
        this.analysisStatus = 'error';
        this.analysisError = '无法获取分析文件路径';
        return;
      }
      
      console.log(`[DEEPSEEK分析] 分析文件路径: ${this.analysisFilePath}`);
      
      // 记录上一次的内容长度，用于检测是否有新内容
      let lastContentLength = 0;
      // 记录轮询次数，用于调试
      let pollCount = 0;
      // 记录最后一次有新内容的时间
      let lastUpdateTime = Date.now();
      // 记录无内容轮询次数
      let emptyContentCount = 0;
      // 最大无更新时间（毫秒），超过这个时间没有更新就认为分析完成
      const maxIdleTime = 30000; // 30秒没有更新则认为完成
      // 最大轮询次数
      const maxPollCount = 120; // 最多轮询120次（约2分钟）
      
      // 创建一个函数来获取文件内容
      const fetchAnalysisFile = () => {
        pollCount++;
        console.log(`[DEEPSEEK分析] 轮询第 ${pollCount} 次: ${this.analysisFilePath}`);
        
        // 每次请求都添加强制刷新参数，确保获取最新内容
        const forceRefresh = true;
        
        AIService.getAnalysisFile(this.analysisFilePath, forceRefresh)
          .then(response => {
            if (response.data && response.data.success) {
              // 如果响应中包含'unchanged'字段，表示文件内容未变化，跳过处理
              if (response.data.unchanged) {
                console.log(`[DEEPSEEK分析] 文件内容未变化，继续轮询`);
                
                // 即使内容未变化，也更新最后轮询时间
                const idleTime = Date.now() - lastUpdateTime;
                let shouldContinuePolling = pollCount < maxPollCount && idleTime < maxIdleTime;
                
                if (shouldContinuePolling) {
                  setTimeout(fetchAnalysisFile, 1000);
                } else {
                  console.log(`[DEEPSEEK分析] 停止轮询，内容未变更已达到阈值`);
                  this.analysisStatus = 'success';
                }
                return;
              }
              
              // 检查内容是否有更新
              const currentContent = response.data.content;
              const isBeingWritten = response.data.is_being_written || false;
              
              // 获取完成状态信息
              const finishStatus = response.data.finish_status || {};
              const isFinished = finishStatus.finished || false;
              const finishReason = finishStatus.finish_reason || null;
              
              if (currentContent.length === 0) {
                emptyContentCount++;
              } else {
                emptyContentCount = 0;
              }
              
              const hasNewContent = currentContent.length > lastContentLength;
              
              // 如果有新内容，更新最后更新时间
              if (hasNewContent || pollCount === 1) {
                lastUpdateTime = Date.now();
                console.log(`[DEEPSEEK分析] 检测到内容更新，长度: ${currentContent.length}`);
                
                // 使用marked处理内容并转换为HTML，与追问逻辑保持一致
                try {
                  const htmlContent = marked(currentContent, {
                    breaks: true,
                    gfm: true,
                    headerIds: false,
                    mangle: false
                  });
                  this.analysisResult = htmlContent;
                } catch (error) {
                  console.error(`[DEEPSEEK分析] 处理内容时出错: ${error.message}`);
                  // 出错时使用原始内容
                  this.analysisResult = currentContent;
                }
                this.analysisStatus = 'loading';
              }
              
              // 更新上次内容长度
              lastContentLength = currentContent.length;
              
              // 计算自上次更新以来的时间
              const idleTime = Date.now() - lastUpdateTime;
              
              // 判断是否应该继续轮询
              let shouldContinuePolling = false;
              
              if (isFinished) {
                // 如果元数据标记已完成，显示详细信息并停止轮询
                console.log(`[DEEPSEEK分析] 接收到完成标志，原因: ${finishReason}`);
                shouldContinuePolling = false;
              } else if (pollCount >= maxPollCount) {
                // 达到最大轮询次数
                console.log(`[DEEPSEEK分析] 达到最大轮询次数 ${maxPollCount}`);
                shouldContinuePolling = false;
              } else if (isBeingWritten) {
                // 文件仍在被写入
                shouldContinuePolling = true;
              } else if (idleTime < maxIdleTime) {
                // 在空闲时间内
                shouldContinuePolling = true;
              } else if (currentContent.length === 0 && emptyContentCount < 15) {
                // 内容为空但次数较少
                shouldContinuePolling = true;
              } else {
                // 默认停止轮询
                console.log(`[DEEPSEEK分析] 达到停止轮询条件: 空闲 ${Math.round(idleTime/1000)}秒, 轮询 ${pollCount} 次`);
                shouldContinuePolling = false;
              }
              
              if (shouldContinuePolling) {
                // 如果内容正在更新，更频繁地轮询
                const pollInterval = hasNewContent ? 500 : 1000;
                setTimeout(fetchAnalysisFile, pollInterval);
              } else {
                // 分析完成
                console.log(`[DEEPSEEK分析] 轮询完成，总共轮询 ${pollCount} 次，完成原因: ${isFinished ? finishReason : '超时/达到轮询上限'}`);
                this.analysisStatus = 'success';
                
                // 确保获取最终完整的分析结果
                AIService.getAnalysisFile(this.analysisFilePath, true)
                  .then(finalResponse => {
                    if (finalResponse.data && finalResponse.data.success && finalResponse.data.content) {
                      console.log(`[DEEPSEEK分析] 获取最终完整内容，长度: ${finalResponse.data.content.length} 字符`);
                      try {
                        const htmlContent = marked(finalResponse.data.content, {
                          breaks: true,
                          gfm: true,
                          headerIds: false,
                          mangle: false
                        });
                        this.analysisResult = htmlContent;
                      } catch (err) {
                        console.error(`[DEEPSEEK分析] 处理最终内容时出错: ${err.message}`);
                        this.analysisResult = finalResponse.data.content;
                      }
                    }
                  })
                  .catch(err => console.error(`[DEEPSEEK分析] 获取最终内容失败: ${err.message}`));
                
                // 确保聊天输入框显示
                this.showChatInput = true;
              }
            } else {
              console.log(`获取分析文件失败: ${response.data.message}`);
              
              // 如果是文件不存在错误，可能是文件还未创建，继续轮询
              if (response.data.message === '文件不存在' && pollCount < 15) {
                setTimeout(fetchAnalysisFile, 1000);
              } else if (pollCount < maxPollCount / 2) {
                // 增加失败时的重试次数
                setTimeout(fetchAnalysisFile, 1000);
              } else {
                // 如果已经轮询了很多次但仍然失败，显示错误信息
                this.analysisStatus = 'error';
                this.analysisError = `获取分析结果失败: ${response.data.message}`;
              }
            }
          })
          .catch(error => {
            console.log("获取分析文件出错: ", error);
            
            // 出错时也继续尝试几次
            if (pollCount < 15) {
              setTimeout(fetchAnalysisFile, 1000);
            } else {
              this.analysisStatus = 'error';
              this.analysisError = '获取分析结果出错，请重试';
            }
          });
      };
      
      // 开始轮询前先等待一小段时间，确保文件已创建
      setTimeout(fetchAnalysisFile, 300);
    },
    
    sendFollowUp() {
      if (!this.followUpMessage.trim() || this.followUpStatus === 'sending') return;
      
      this.followUpStatus = 'sending';
      
      // 获取当前的HTML内容
      const currentHtml = this.analysisResult || '';
      
      // 创建追问的Markdown文本
      let followUpMarkdown = '\n\n---\n\n';
      followUpMarkdown += '**我**：' + this.followUpMessage + '\n\n';
      followUpMarkdown += '**DEEPSEEK**：*思考中...*';
      
      // 使用marked将Markdown转换为HTML并附加到现有内容
      const appendedHtml = currentHtml + marked(followUpMarkdown, { 
        breaks: true, 
        gfm: true,
        headerIds: false,
        mangle: false 
      });
      
      // 保存原始完整内容，以便在失败时恢复
      const originalContent = this.analysisResult;
      
      // 更新UI显示，添加用户问题和思考中状态
      this.analysisResult = appendedHtml;
      
      // 记录当前滚动位置
      const analysisContent = document.querySelector('.analysis-content');
      const scrollPosition = analysisContent ? analysisContent.scrollTop : 0;
      
      AIService.sendFollowUp(this.followUpMessage, this.selectedModel)
        .then(response => {
          if (response.data && response.data.success) {
            this.followUpStatus = 'success';
            
            // 获取更新后的分析结果文件路径
            this.analysisFilePath = response.data.response_path;
            console.log(`[DEEPSEEK追问] 成功发送追问，将从文件获取回复: ${this.analysisFilePath}`);
            
            // 开始轮询获取回复
            this.pollFollowUpResponse();
            
            // 清空输入框
            this.followUpMessage = '';
            
            // 滚动到底部，显示新内容
            setTimeout(() => {
              if (analysisContent) {
                analysisContent.scrollTop = analysisContent.scrollHeight;
              }
            }, 100);
          } else {
            // 恢复原始内容
            this.analysisResult = originalContent;
            
            if (response.data.message) {
              alert(response.data.message || '发送追问失败');
            }
            else throw new Error(response.data.message || '发送追问失败');
          }
        })
        .catch(error => {
          this.followUpStatus = 'error';
          console.error('Follow-up error:', error);
          
          // 恢复原始内容
          this.analysisResult = originalContent;
          
          // 附加错误消息
          setTimeout(() => {
            const errorHtml = marked('\n\n---\n\n**错误**：发送追问失败，请重试', { 
              breaks: true, 
              gfm: true 
            });
            this.analysisResult += errorHtml;
            
            // 恢复滚动位置
            if (analysisContent) {
              analysisContent.scrollTop = scrollPosition;
            }
          }, 100);
          
          // 延迟重置状态，以便用户可以再次尝试
          setTimeout(() => {
            this.followUpStatus = null;
          }, 2000);
        });
    },
    
    pollFollowUpResponse() {
      if (!this.analysisFilePath) return;
      
      let lastContentLength = 0;
      let pollCount = 0;
      let lastUpdateTime = Date.now();
      let emptyContentCount = 0;
      const maxIdleTime = 30000; // 增加到30秒
      const maxPollCount = 120; // 最多轮询120次
      
      const fetchResponseFile = () => {
        pollCount++;
        
        // 每次请求都添加强制刷新参数，确保获取最新内容
        const forceRefresh = true;
        
        AIService.getAnalysisFile(this.analysisFilePath, forceRefresh)
          .then(response => {
            if (response.data && response.data.success) {
              // 如果响应中包含'unchanged'字段，表示文件内容未变化，跳过处理
              if (response.data.unchanged) {
                console.log(`[DEEPSEEK追问] 文件内容未变化，继续轮询`);
                
                // 即使内容未变化，也更新最后轮询时间
                const idleTime = Date.now() - lastUpdateTime;
                let shouldContinuePolling = pollCount < maxPollCount && idleTime < maxIdleTime;
                
                if (shouldContinuePolling) {
                  setTimeout(fetchResponseFile, 1000);
                } else {
                  console.log(`[DEEPSEEK追问] 停止轮询，内容未变更已达到阈值`);
                  this.followUpStatus = null;
                }
                return;
              }
              
              const currentContent = response.data.content;
              const isBeingWritten = response.data.is_being_written || false;
              
              // 获取完成状态信息
              const finishStatus = response.data.finish_status || {};
              const isFinished = finishStatus.finished || false;
              const finishReason = finishStatus.finish_reason || null;
              
              if (currentContent.length === 0) {
                emptyContentCount++;
              } else {
                emptyContentCount = 0;
              }
              
              const hasNewContent = currentContent.length > lastContentLength;
              
              if (hasNewContent || pollCount === 1) {
                lastUpdateTime = Date.now();
                console.log(`[DEEPSEEK追问] 检测到新内容，长度从 ${lastContentLength} 增加到 ${currentContent.length}`);
                
                try {
                  // 尝试提取AI回复部分
                  let formattedContent = currentContent;
                  const userMsgIndex = formattedContent.lastIndexOf(`我：${this.followUpMessage}`);
                  
                  if (userMsgIndex !== -1) {
                    const deepseekIndex = formattedContent.indexOf("DEEPSEEK：", userMsgIndex);
                    if (deepseekIndex !== -1) {
                      // 获取DEEPSEEK的回复部分
                      const responseText = formattedContent.substring(deepseekIndex + 9).trim();
                      
                      // 查找思考中...的HTML元素
                      const thinkingPattern = /<em>思考中\.\.\.<\/em>/;
                      
                      // 替换思考中...为实际回复的内容，正确处理Markdown格式
                      if (this.analysisResult.includes('<em>思考中...</em>')) {
                        // 使用marked库直接将Markdown转换为HTML
                        const responseHtml = marked(responseText, {
                          breaks: true,
                          gfm: true,
                          headerIds: false,
                          mangle: false
                        });
                        
                        // 替换思考中...为实际回复
                        this.analysisResult = this.analysisResult.replace(thinkingPattern, responseHtml);
                        console.log(`[DEEPSEEK追问] 成功更新回复内容，长度: ${responseText.length}`);
                      } else {
                        // 如果无法找到思考中...标记，尝试使用完整内容重建响应
                        console.log(`[DEEPSEEK追问] 未找到思考中...标记，使用完整内容更新`);
                        
                        // 处理全部内容的Markdown格式
                        const fullHtml = marked(formattedContent, {
                          breaks: true,
                          gfm: true,
                          headerIds: false,
                          mangle: false
                        });
                        
                        // 直接使用完整的HTML内容
                        this.analysisResult = fullHtml;
                        console.log(`[DEEPSEEK追问] 使用完整HTML内容更新，长度: ${fullHtml.length}`);
                      }
                    } else {
                      console.log(`[DEEPSEEK追问] 找到用户消息但没有找到DEEPSEEK回复标记`);
                      // 第一次轮询或内容显著变更时，直接使用完整内容
                      if (pollCount === 1 || (currentContent.length - lastContentLength) > 50) {
                        const fullHtml = marked(formattedContent, {
                          breaks: true,
                          gfm: true,
                          headerIds: false,
                          mangle: false
                        });
                        this.analysisResult = fullHtml;
                        console.log(`[DEEPSEEK追问] 使用完整内容(无DEEPSEEK标记)更新，长度: ${formattedContent.length}`);
                      }
                    }
                  } else {
                    console.log(`[DEEPSEEK追问] 无法在内容中找到用户消息: ${this.followUpMessage.substring(0, 20)}...`);
                    // 第一次轮询或者内容发生明显变化时，直接使用完整内容
                    if (pollCount === 1 || (currentContent.length - lastContentLength) > 50) {
                      const fullHtml = marked(formattedContent, {
                        breaks: true,
                        gfm: true,
                        headerIds: false,
                        mangle: false
                      });
                      this.analysisResult = fullHtml;
                      console.log(`[DEEPSEEK追问] 使用完整内容(无用户消息)更新，长度: ${formattedContent.length}`);
                    }
                  }
                } catch (error) {
                  console.error(`[DEEPSEEK追问] 解析回复内容时出错: ${error.message}`);
                  // 出错时直接使用完整内容
                  try {
                    const fullHtml = marked(currentContent, {
                      breaks: true,
                      gfm: true,
                      headerIds: false,
                      mangle: false
                    });
                    this.analysisResult = fullHtml;
                    console.log(`[DEEPSEEK追问] 解析出错，使用完整内容更新: ${currentContent.length} 字符`);
                  } catch (err) {
                    console.error(`[DEEPSEEK追问] 处理完整内容时再次出错: ${err.message}`);
                  }
                }
                
                // 即使解析特定格式失败，也尝试显示新内容
                if (this.analysisResult.includes('<em>思考中...</em>') || this.analysisResult.includes('思考中...')) {
                  // 如果还存在"思考中..."，尝试用原始内容替换
                  try {
                    // 获取新增的原始内容部分
                    const newRawContent = currentContent.substring(lastContentLength);
                    if (newRawContent.trim()) {
                      console.log(`[DEEPSEEK追问] 使用原始内容更新: ${newRawContent.substring(0, 50)}...`);
                      // 将思考中...替换为新内容，但保持简单格式
                      this.analysisResult = this.analysisResult.replace(
                        /<em>思考中\.\.\.<\/em>|思考中\.\.\./,
                        newRawContent
                      );
                    }
                  } catch (err) {
                    console.error(`[DEEPSEEK追问] 更新原始内容时出错: ${err.message}`);
                  }
                }
                
                lastContentLength = currentContent.length;
              }
              
              const idleTime = Date.now() - lastUpdateTime;
              
              // 判断是否应该继续轮询
              let shouldContinuePolling = false;
              
              if (isFinished) {
                // 如果元数据标记已完成，停止轮询
                console.log(`[DEEPSEEK追问] 接收到完成标志，原因: ${finishReason}`);
                shouldContinuePolling = false;
              } else if (pollCount >= maxPollCount) {
                // 达到最大轮询次数
                console.log(`[DEEPSEEK追问] 达到最大轮询次数 ${maxPollCount}`);
                shouldContinuePolling = false;
              } else if (isBeingWritten) {
                // 文件仍在被写入
                console.log(`[DEEPSEEK追问] 文件仍在被写入`);
                shouldContinuePolling = true;
              } else if (idleTime < maxIdleTime) {
                // 在空闲时间内
                console.log(`[DEEPSEEK追问] 在空闲时间内 (${Math.round(idleTime/1000)}秒/${Math.round(maxIdleTime/1000)}秒)`);
                shouldContinuePolling = true;
              } else if (currentContent.length === 0 && emptyContentCount < 15) {
                // 内容为空但次数较少
                console.log(`[DEEPSEEK追问] 内容为空但次数较少 (${emptyContentCount}/15)`);
                shouldContinuePolling = true;
              } else {
                // 默认行为 - 未捕获的条件
                console.log(`[DEEPSEEK追问] 达到停止轮询条件: 空闲 ${Math.round(idleTime/1000)}秒, 轮询 ${pollCount} 次`);
                shouldContinuePolling = false;
              }
              
              if (shouldContinuePolling) {
                setTimeout(fetchResponseFile, 1000);
              } else {
                console.log(`[DEEPSEEK追问] 轮询完成，总共轮询 ${pollCount} 次，完成原因: ${isFinished ? finishReason : '超时/达到轮询上限'}`);
                this.followUpStatus = null;
                
                // 确保在轮询结束时获取一次最新完整内容
                AIService.getAnalysisFile(this.analysisFilePath, true)
                  .then(finalResponse => {
                    if (finalResponse.data && finalResponse.data.success && finalResponse.data.content) {
                      console.log(`[DEEPSEEK追问] 获取最终完整内容，长度: ${finalResponse.data.content.length} 字符`);
                      const fullHtml = marked(finalResponse.data.content, {
                        breaks: true,
                        gfm: true,
                        headerIds: false,
                        mangle: false
                      });
                      this.analysisResult = fullHtml;
                    }
                  })
                  .catch(err => console.error(`[DEEPSEEK追问] 获取最终内容失败: ${err.message}`));
                
                // 如果轮询结束但仍显示"思考中..."，替换为适当消息
                if (this.analysisResult.includes('<em>思考中...</em>')) {
                  this.analysisResult = this.analysisResult.replace(
                    /<em>思考中\.\.\.<\/em>/,
                    '<span style="color: #10b981;">回复已完成</span>'
                  );
                }
              }
            } else {
              if (response.data.message === '文件不存在' && pollCount < 15) {
                console.log(`[DEEPSEEK追问] 文件不存在 && pollCount < 15，继续轮询`);
                setTimeout(fetchResponseFile, 1000);
              } else if (pollCount < 15) {
                console.log(`[DEEPSEEK追问] 继续轮询`);
                setTimeout(fetchResponseFile, 1000);
              } else {
                console.log(`[DEEPSEEK追问] 获取响应失败，请重试`);
                this.followUpStatus = null;
                // 从分析结果中移除"思考中..."
                this.analysisResult = this.analysisResult.replace(
                  '<em>思考中...</em>', 
                  '<span style="color: #ef4444;">获取响应失败，请重试</span>'
                );
              }
            }
          })
          .catch((error) => {
            console.error(`[DEEPSEEK追问] 请求出错: ${error.message || '未知错误'}`);
            if (pollCount < 15) {
              setTimeout(fetchResponseFile, 1000);
            } else {
              this.followUpStatus = null;
              // 从分析结果中移除"思考中..."
              this.analysisResult = this.analysisResult.replace(
                '<em>思考中...</em>', 
                '<span style="color: #ef4444;">获取响应失败，请重试</span>'
              );
            }
          });
      };
      
      // 开始轮询前稍微延迟，确保分析文件已更新
      setTimeout(fetchResponseFile, 1000);
    },
    
    startTimer() {
      this.recordingInterval = setInterval(() => {
        this.recordingTime++;
      }, 1000);
    },
    
    // 修改更新分析内容的方法，使用标准样式
    updateAnalysisContent(message) {
      console.log("更新分析内容:", message);
      
      // 创建状态消息的HTML
      const statusMessageHtml = `<div class="status-message">${message}</div>`;
      
      // 如果是要替换内容而不是追加，则直接设置
      if (!this.analysisResult || this.analysisResult.trim() === '') {
        this.analysisResult = statusMessageHtml;
      } else {
        // 如果内容已经是HTML格式，直接添加新的状态消息HTML
        this.analysisResult += statusMessageHtml;
      }
    },
  }
};
</script>

<style>
:root {
  --primary: #6366f1;  /* 专业蓝紫色调 */
  --gradient: linear-gradient(135deg, #8b5cf6 0%, #3b82f6 100%);
  --success: #10b981;
  --warning: #f59e0b;
  --danger: #ef4444;
}

.container {
  max-width: 900px;
  margin: 0 auto;
  padding: 2rem;
}

.glass-card {
  background: rgba(255, 255, 255, 0.95);
  backdrop-filter: blur(12px);
  border-radius: 24px;
  box-shadow: 0 8px 32px rgba(99, 102, 241, 0.1);
  padding: 2.5rem;
  height: 100%;
  margin-bottom: 1rem;
  border: 1px solid rgba(255,255,255,0.2);
  transition: all 0.3s ease;
}

/* Dark theme styles for glass-card */
[data-theme="dark"] .glass-card {
  background: rgba(30, 30, 30, 0.95);
  border-color: rgba(50, 50, 50, 0.3);
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.2);
}

.glass-card:hover {
  box-shadow: 0 12px 40px rgba(99, 102, 241, 0.15);
  transform: translateY(-2px);
}

[data-theme="dark"] .glass-card:hover {
  box-shadow: 0 12px 40px rgba(0, 0, 0, 0.3);
}

.question-card {
  position: relative;
  overflow: hidden;
}

.question-card::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  width: 6px;
  height: 100%;
  background: var(--gradient);
  border-radius: 3px;
}

.question-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 0.75rem;
  padding-left: 1rem;
}

[data-theme="dark"] .question-title {
  color: #94a3b8;
}

.question-content {
  font-size: 1.25rem;
  font-weight: 500;
  color: #1e293b;
  line-height: 1.7;
  padding-left: 1rem;
}

[data-theme="dark"] .question-content {
  color: #e2e8f0;
}

.analysis-card {
  min-height: 500px;
}

.analysis-title {
  font-size: 1.1rem;
  font-weight: 600;
  color: #64748b;
  margin-bottom: 1.5rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

[data-theme="dark"] .analysis-title {
  color: #94a3b8;
}

.analysis-title svg {
  width: 20px;
  height: 20px;
}

.analysis-content {
  background: rgba(241, 245, 249, 0.7);
  border-radius: 16px;
  padding: 1.5rem;
  font-size: 1.1rem;
  color: #334155;
  height: 400px;
  border: 1px dashed #cbd5e1;
  overflow-y: auto;
  scrollbar-width: thin;
  scrollbar-color: rgba(99, 102, 241, 0.3) rgba(241, 245, 249, 0.5);
}

/* Markdown样式 */
.analysis-content h1, 
.analysis-content h2, 
.analysis-content h3, 
.analysis-content h4, 
.analysis-content h5, 
.analysis-content h6 {
  margin-top: 1.5rem;
  margin-bottom: 1rem;
  color: #1e293b;
  font-weight: 600;
  line-height: 1.3;
}

.analysis-content h1 {
  font-size: 1.8rem;
}

.analysis-content h2 {
  font-size: 1.5rem;
  border-bottom: 1px solid rgba(99, 102, 241, 0.2);
  padding-bottom: 0.5rem;
}

.analysis-content h3 {
  font-size: 1.25rem;
  color: #6366f1;
}

.analysis-content h4 {
  font-size: 1.1rem;
}

.analysis-content p {
  margin-bottom: 1rem;
  line-height: 1.6;
}

.analysis-content ul, 
.analysis-content ol {
  margin-bottom: 1rem;
  padding-left: 1.5rem;
}

.analysis-content li {
  margin-bottom: 0.5rem;
}

.analysis-content a {
  color: #6366f1;
  text-decoration: none;
}

.analysis-content a:hover {
  text-decoration: underline;
}

.analysis-content blockquote {
  border-left: 4px solid #6366f1;
  padding-left: 1rem;
  margin-left: 0;
  margin-right: 0;
  font-style: italic;
  color: #64748b;
}

.analysis-content code {
  font-family: monospace;
  background: rgba(203, 213, 225, 0.3);
  padding: 0.2rem 0.4rem;
  border-radius: 4px;
  font-size: 0.9em;
}

.analysis-content pre {
  background: rgba(30, 41, 59, 0.9);
  color: #e2e8f0;
  padding: 1rem;
  border-radius: 8px;
  overflow-x: auto;
  margin-bottom: 1rem;
}

.analysis-content pre code {
  background: transparent;
  color: inherit;
  padding: 0;
}

.analysis-content hr {
  margin: 2rem 0;
  border: 0;
  height: 1px;
  background: rgba(99, 102, 241, 0.2);
}

.analysis-content table {
  width: 100%;
  border-collapse: collapse;
  margin-bottom: 1rem;
}

.analysis-content th, 
.analysis-content td {
  border: 1px solid rgba(203, 213, 225, 0.5);
  padding: 0.5rem;
}

.analysis-content th {
  background: rgba(241, 245, 249, 0.7);
  font-weight: 600;
}

.analysis-content img {
  max-width: 100%;
  border-radius: 8px;
  margin: 1rem 0;
}

/* Dark主题对应的Markdown样式 */
[data-theme="dark"] .analysis-content h1,
[data-theme="dark"] .analysis-content h2,
[data-theme="dark"] .analysis-content h3,
[data-theme="dark"] .analysis-content h4,
[data-theme="dark"] .analysis-content h5,
[data-theme="dark"] .analysis-content h6 {
  color: #e2e8f0;
}

[data-theme="dark"] .analysis-content blockquote {
  color: #94a3b8;
}

[data-theme="dark"] .analysis-content code {
  background: rgba(51, 65, 85, 0.5);
}

[data-theme="dark"] .analysis-content th {
  background: rgba(51, 65, 85, 0.5);
}

[data-theme="dark"] .analysis-content {
  background: rgba(30, 41, 59, 0.7);
  border-color: #475569;
  color: #e2e8f0;
  scrollbar-color: rgba(99, 102, 241, 0.5) rgba(30, 41, 59, 0.8);
}

.analysis-content::-webkit-scrollbar {
  width: 8px;
}

.analysis-content::-webkit-scrollbar-track {
  background: rgba(241, 245, 249, 0.5);
  border-radius: 10px;
}

[data-theme="dark"] .analysis-content::-webkit-scrollbar-track {
  background: rgba(30, 41, 59, 0.5);
}

.analysis-content::-webkit-scrollbar-thumb {
  background: rgba(99, 102, 241, 0.3);
  border-radius: 10px;
}

.analysis-content::-webkit-scrollbar-thumb:hover {
  background: rgba(99, 102, 241, 0.5);
}

.analysis-placeholder {
  color: #94a3b8;
  font-style: italic;
  text-align: center;
  padding: 2rem 0;
}

[data-theme="dark"] .analysis-placeholder {
  color: #64748b;
}

.analysis-controls {
  margin-top: 20px;
  padding-top: 15px;
  border-top: 1px solid rgba(99, 102, 241, 0.1);
  display: flex;
  justify-content: center;
  gap: 1rem;
}

[data-theme="dark"] .analysis-controls {
  border-top-color: rgba(99, 102, 241, 0.2);
}

.btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  padding: 1rem 2rem;
  border-radius: 16px;
  font-size: 1.1rem;
  font-weight: 600;
  border: none;
  cursor: pointer;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
}

.btn svg {
  width: 20px;
  height: 20px;
}

.btn-primary {
  background: var(--gradient);
  color: white;
}

.btn-primary:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(99, 102, 241, 0.2);
}

.btn-primary:active {
  transform: translateY(0);
  box-shadow: 0 2px 4px -1px rgba(99, 102, 241, 0.1);
}

.btn-warning {
  background: linear-gradient(135deg, #fbbf24 0%, #f59e0b 100%);
  color: white;
}

.btn-warning:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(245, 158, 11, 0.2);
}

.btn-success {
  background: linear-gradient(135deg, #34d399 0%, #10b981 100%);
  color: white;
}

.btn-success:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(16, 185, 129, 0.2);
}

.btn-danger {
  background: linear-gradient(135deg, #f87171 0%, #ef4444 100%);
  color: white;
}

.btn-danger:hover {
  transform: translateY(-2px);
  box-shadow: 0 10px 15px -3px rgba(239, 68, 68, 0.2);
}

.recording-indicator {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  margin-top: 1rem;
  justify-content: center;
  font-weight: 500;
  color: #ef4444;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.recording-indicator.active {
  opacity: 1;
}

.pulse {
  width: 12px;
  height: 12px;
  border-radius: 50%;
  background: #ef4444;
  position: relative;
}

.pulse::after {
  content: '';
  position: absolute;
  width: 100%;
  height: 100%;
  top: 0;
  left: 0;
  background: rgba(239, 68, 68, 0.5);
  border-radius: 50%;
  animation: pulse 1.5s infinite;
}

@keyframes pulse {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(3);
    opacity: 0;
  }
}

.timer {
  font-family: monospace;
  font-size: 1.1rem;
}

/* 聊天输入框样式 */
.chat-input-container {
  display: flex;
  gap: 0.5rem;
  margin-top: 0.5rem;
  width: 100%;
  max-width: 900px;
  margin-left: auto;
  margin-right: auto;
}

.chat-input {
  flex: 1;
  padding: 1rem 1.5rem;
  border-radius: 16px;
  border: 1px solid rgba(203, 213, 225, 0.5);
  background: rgba(255, 255, 255, 0.8);
  font-size: 1rem;
  transition: all 0.3s ease;
  outline: none;
}

.chat-input:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.2);
}

.send-btn {
  background: var(--gradient);
  color: white;
  border: none;
  border-radius: 16px;
  width: 50px;
  height: 50px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.send-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 6px -1px rgba(99, 102, 241, 0.2);
}

.send-btn svg {
  width: 20px;
  height: 20px;
}

.send-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* 状态消息样式 */
.status-message {
  color: #6366f1;
  font-style: italic;
  margin: 0.5rem 0;
  padding: 0.5rem;
  background: rgba(99, 102, 241, 0.1);
  border-radius: 8px;
  text-align: center;
}

/* 分析分隔线 */
.analysis-divider {
  margin: 1rem 0;
  border: 0;
  height: 1px;
  background: rgba(99, 102, 241, 0.2);
}

/* 新增样式：括号内容蓝紫色显示并左对齐 */
.bracketed-content {
  color: #6366f1; /* 蓝紫色 */
  display: block;
  text-align: left;
  font-weight: 500;
}

/* 新增样式：DEEPSEEK分析标题加大 */
.deepseek-analysis-title {
  color: #6366f1; /* 蓝紫色 */
  display: block;
  text-align: left;
  font-weight: 700;
  font-size: 1.5em;
}

/* 添加deepseek-section容器样式 */
.deepseek-section {
  display: block;
  text-align: left;
  margin-bottom: 8px;
  margin-top: 8px;
}

/* 处理列表项的样式 */
.list-item {
  display: block;
  text-align: left;
  margin-left: 0;
  padding-left: 0;
}

/* 确保所有markdown列表项都左对齐 */
.analysis-content ul,
.analysis-content ol,
.analysis-content p {
  text-align: left;
}

/* 处理markdown中的序号列表 */
.analysis-result ol {
  list-style-position: inside;
  padding-left: 0;
  margin-left: 0;
}

/* 添加题目信息样式 */
.problem-info {
  margin-top: 1rem;
  font-size: 0.9rem;
  color: #64748b;
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem;
  padding-left: 1rem;
  justify-content: flex-end;
  padding-right: 1rem;
}

.problem-info-item {
  background: rgba(241, 245, 249, 0.7);
  padding: 0.25rem 0.75rem;
  border-radius: 8px;
  display: flex;
  align-items: center;
  gap: 0.25rem;
}

.problem-info-item i {
  font-size: 0.8rem;
  opacity: 0.7;
}

.model-selector {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-left: auto;
  background: rgba(241, 245, 249, 0.7);
  padding: 0.3rem 0.6rem;
  border-radius: 8px;
  font-size: 0.85rem;
  border: 1px solid rgba(203, 213, 225, 0.4);
}

.switch {
  position: relative;
  display: inline-block;
  width: 42px;
  height: 22px;
}

.switch input {
  opacity: 0;
  width: 0;
  height: 0;
}

.slider {
  position: absolute;
  cursor: pointer;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: #94a3b8;
  -webkit-transition: .3s;
  transition: .3s;
}

.slider:before {
  position: absolute;
  content: "";
  height: 16px;
  width: 16px;
  left: 3px;
  bottom: 3px;
  background-color: white;
  -webkit-transition: .3s;
  transition: .3s;
}

input:checked + .slider {
  background-color: #6366f1;
}

input:focus + .slider {
  box-shadow: 0 0 1px #6366f1;
}

input:checked + .slider:before {
  -webkit-transform: translateX(20px);
  -ms-transform: translateX(20px);
  transform: translateX(20px);
}

.slider.round {
  border-radius: 20px;
}

.slider.round:before {
  border-radius: 50%;
}

/* 全局背景样式 */
body {
  background: radial-gradient(circle at 10% 20%, rgba(99,102,241,0.1) 0%, rgba(255,255,255,1) 100%);
  min-height: 100vh;
  font-family: 'Inter', system-ui, -apple-system, sans-serif;
  color: #1e293b;
  line-height: 1.6;
}

/* 响应式调整 */
@media (max-width: 768px) {
  .container {
    padding: 1rem;
  }
  
  .glass-card {
    padding: 1.5rem;
    border-radius: 16px;
  }
  
  .analysis-controls {
    flex-direction: column;
    gap: 0.75rem;
  }
  
  .btn {
    width: 100%;
    padding: 0.875rem 1.5rem;
    font-size: 1rem;
  }
}

/* 移除旧的上传状态样式 */
.upload-status {
  display: none;
}

.loading-spinner {
  display: none;
}

/* 添加标题样式 */
.section-heading {
  color: var(--primary);
  font-size: 1.5rem;
  font-weight: 600;
  margin: 1.8rem 0 1.2rem;
  padding-bottom: 0.6rem;
  border-bottom: 1px solid rgba(99, 102, 241, 0.2);
}

/* Dark theme for problem-info */
[data-theme="dark"] .problem-info {
  color: #94a3b8;
}

[data-theme="dark"] .problem-info-item {
  background: rgba(30, 41, 59, 0.7);
  color: #cbd5e1;
}

/* Dark theme for section headings and analysis result */
[data-theme="dark"] .section-heading {
  color: #e2e8f0;
}

[data-theme="dark"] .analysis-result strong {
  color: #e2e8f0;
}

[data-theme="dark"] .status-message {
  background: rgba(99, 102, 241, 0.2);
  color: #a5b4fc;
}

/* Dark theme for chat input */
[data-theme="dark"] .chat-input {
  background: rgba(30, 41, 59, 0.8);
  border-color: rgba(71, 85, 105, 0.5);
  color: #e2e8f0;
}

[data-theme="dark"] .chat-input:focus {
  border-color: var(--primary);
  box-shadow: 0 0 0 2px rgba(99, 102, 241, 0.3);
}

/* Dark theme styles for fallback input container */
[data-theme="dark"] #fallbackInputContainer textarea {
  background: rgba(30, 41, 59, 0.8);
  border-color: rgba(71, 85, 105, 0.5);
  color: #e2e8f0;
}
</style>
